/**
 * What Home does with files: the ones Docs, Sheets and Slides open are imported (importFiles.ts),
 * and anything else is refused with a list of what is accepted.
 *
 * One file opens its document when it is ready. Several are imported one after another and
 * leave a toast with the last one's link, so a drop of ten files does not throw the person
 * into the tenth.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { errorText } from '../api';
import type { Toast } from '../core/ui';
import { prefetchEditor } from '../core/prefetch';
import type { DocKind } from '@shared/api';
import { docPath, importKindFor } from './home';
import type { ImportProgress } from './ImportStatus';

interface Options {
  navigate: (path: string) => void;
  notify: (toast: Toast) => void;
  /** Reload the list: something new is in it. */
  reload: () => void;
}

export function useImportFiles({ navigate, notify, reload }: Options): {
  takeFiles: (files: FileList | File[]) => void;
  /** A file is about to arrive: start what reading it needs. */
  warm: () => void;
  importing: ImportProgress | null;
} {
  const [importing, setImporting] = useState<ImportProgress | null>(null);
  const alive = useRef(true);
  const busy = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const importAll = useCallback(
    async (files: File[]): Promise<void> => {
      busy.current = true;
      // The editor's code loads while the file is read, so opening the result is not a second wait.
      prefetchEditor(importKindFor(files[0]?.name ?? '') ?? 'docs');
      try {
        const { ImportFailed, importFile, importSheetFile, importSlidesFile } = await import('./importFiles');
        const importers = { docs: importFile, sheets: importSheetFile, slides: importSlidesFile } as const;
        let last: { id: string; title: string; kind: DocKind } | null = null;
        let opened = 0;
        for (const [index, file] of files.entries()) {
          setImporting({ name: file.name, index, count: files.length, stage: 'reading' });
          try {
            const kind = importKindFor(file.name) ?? 'docs';
            const result = await importers[kind](file, (stage) => setImporting((p) => (p ? { ...p, stage } : p)));
            opened++;
            last = { id: result.doc.id, title: result.doc.title, kind };
            if (files.length === 1) {
              const note = result.notes[0];
              if (alive.current) navigate(docPath(kind, result.doc.id));
              else notify({ text: `Opened “${result.doc.title}”.`, action: { label: 'Open', run: () => navigate(docPath(kind, result.doc.id)) } });
              if (note) notify({ text: note });
            } else if (result.notes[0]) {
              notify({ text: `${file.name}: ${result.notes[0]}` });
            }
          } catch (err) {
            const why = err instanceof ImportFailed ? err.message : errorText(err, 'That file could not be opened.');
            notify({ text: `${file.name}: ${why}`, tone: 'danger' });
          }
        }
        if (files.length > 1 && opened > 0 && last) {
          const target = last;
          notify({
            text: opened === 1 ? `Opened “${target.title}”.` : `Opened ${opened} documents.`,
            action: { label: 'Open', run: () => navigate(docPath(target.kind, target.id)) }
          });
        }
        if (opened > 0) reload();
      } finally {
        busy.current = false;
        if (alive.current) setImporting(null);
      }
    },
    [navigate, notify, reload]
  );

  const takeFiles = useCallback(
    (files: FileList | File[]) => {
      const list = [...files];
      if (list.length === 0) return;
      if (busy.current) {
        notify({ text: 'Still opening the last file.' });
        return;
      }
      const mine = list.filter((f) => importKindFor(f.name));
      if (mine.length > 0) void importAll(mine);
      if (mine.length < list.length) notify({ text: 'Office opens .docx, .md, .html, .xlsx, .csv, .tsv, .pptx, .mvdoc, .mvsheet and .mvslides files.' });
    },
    [importAll, notify]
  );

  /** The picker is opening or a drag has entered: load the worker and the editor while the person chooses. */
  const warm = useCallback(() => {
    if (busy.current) return;
    prefetchEditor('docs');
    void import('../formats/browser/importClient').then((m) => m.warmUp());
  }, []);

  return { takeFiles, warm, importing };
}
